import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonBase,
  Checkbox,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import LayersIcon from '@material-ui/icons/Layers';
import GroupIcon from '@material-ui/icons/Group';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import SearchIcon from '@material-ui/icons/Search';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import {
  Entity,
  RELATION_OWNED_BY,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { FluxButton, FluxChip, MONO_FONT, fluxTokens } from '../../flux';
import {
  CatalogQueryResult,
  CatalogQueryState,
  PAGE_SIZES,
  SortField,
} from './useCatalogQuery';
import { entityIcon, humanizeName, kindLabels, lifecycleTone, shortRef } from './presentation';

export type ColumnId = 'kind' | 'system' | 'owner' | 'type' | 'lifecycle' | 'tags';

export const COLUMN_LABELS: Record<ColumnId, string> = {
  kind: 'Kind',
  system: 'System',
  owner: 'Owner',
  type: 'Type',
  lifecycle: 'Lifecycle',
  tags: 'Tags',
};

const COLUMN_KINDS: Record<ColumnId, string[] | undefined> = {
  kind: undefined,
  system: ['component', 'api', 'resource'],
  owner: ['component', 'api', 'resource', 'system', 'domain', 'template'],
  type: undefined,
  lifecycle: ['component', 'api'],
  tags: undefined,
};

const SORTABLE: Partial<Record<ColumnId, SortField>> = {
  system: 'system',
  owner: 'owner',
  type: 'type',
  lifecycle: 'lifecycle',
};

export function useColumnPrefs(storageKey: string, defaults: ColumnId[]) {
  const [columns, setColumns] = useState<ColumnId[]>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
      return Array.isArray(stored) ? stored : defaults;
    } catch {
      return defaults;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch {
      // ignore
    }
  }, [columns, storageKey]);
  return [columns, setColumns] as const;
}

function columnApplies(id: ColumnId, kind: string) {
  return !kind || !COLUMN_KINDS[id] || COLUMN_KINDS[id]!.includes(kind);
}

export function applicableColumns(kind: string, selected: ColumnId[]): ColumnId[] {
  return (Object.keys(COLUMN_LABELS) as ColumnId[]).filter(
    id => selected.includes(id) && columnApplies(id, kind),
  );
}

const useStyles = makeStyles(theme => {
  const t = fluxTokens(theme);
  return {
    wrap: {
      position: 'relative',
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
    },
    progress: {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      height: 2,
      background: 'transparent',
      '& .MuiLinearProgress-bar': { background: t.accentStrong },
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      fontSize: 12.5,
      color: t.text,
      '& th': {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        height: 36,
        padding: '0 14px',
        textAlign: 'left',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: t.textMuted,
        background: t.pageBg,
        borderBottom: `1px solid ${t.border}`,
        whiteSpace: 'nowrap',
      },
      '& td': {
        height: 48,
        padding: '6px 14px',
        borderBottom: `1px solid ${t.border}`,
        verticalAlign: 'middle',
      },
      '& tbody tr': {
        cursor: 'pointer',
        transition: 'background-color 0.12s ease',
      },
      '& tbody tr:hover': { background: t.surfaceHover },
      '& tbody tr:hover $star': { opacity: 1 },
    },
    sortBtn: {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      letterSpacing: 'inherit',
      textTransform: 'inherit',
      color: 'inherit',
      gap: 4,
      '& svg': { fontSize: 14 },
      '&:hover': { color: t.text },
    },
    sortActive: { color: t.accent },
    sortDesc: { transform: 'rotate(180deg)' },
    nameCell: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minWidth: 240,
      maxWidth: 460,
    },
    iconTile: {
      flexShrink: 0,
      width: 26,
      height: 26,
      borderRadius: 7,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: t.accent,
      background: t.accentBg,
      border: `1px solid ${t.dark ? 'rgba(139,92,246,0.3)' : 'rgba(110,58,143,0.18)'}`,
      '& svg': { fontSize: 15 },
    },
    nameText: { minWidth: 0 },
    name: {
      display: 'block',
      fontWeight: 600,
      color: t.dark ? '#d8c9ff' : '#552569',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    desc: {
      display: 'block',
      marginTop: 2,
      fontSize: 11.5,
      color: t.textSecondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    owner: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      color: t.text,
      whiteSpace: 'nowrap',
      '& svg': { fontSize: 15, color: t.textMuted },
    },
    muted: { color: t.textMuted },
    tagsCell: { display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 220 },
    star: {
      opacity: 0,
      padding: 4,
      color: t.textMuted,
      transition: 'opacity 0.12s ease',
      '& svg': { fontSize: 17 },
    },
    starOn: { opacity: 1, color: '#f5b82e' },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 12,
      padding: 16,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 10,
      padding: 14,
      borderRadius: 10,
      border: `1px solid ${t.border}`,
      background: t.surface,
      textAlign: 'left',
      fontFamily: 'inherit',
      transition: 'border-color 0.15s ease, transform 0.15s ease',
      '&:hover': { borderColor: t.accentBorder },
      '&:hover $star': { opacity: 1 },
    },
    cardHead: { display: 'flex', alignItems: 'center', gap: 10, width: '100%' },
    cardDesc: {
      fontSize: 12,
      color: t.textSecondary,
      lineHeight: 1.45,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      minHeight: 34,
    },
    cardChips: { display: 'flex', flexWrap: 'wrap', gap: 5 },
    cardFoot: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 8,
      borderTop: `1px solid ${t.border}`,
      fontSize: 12,
    },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      padding: '64px 16px',
      color: t.textSecondary,
      fontSize: 13,
      '& > svg': { fontSize: 36, color: t.textMuted },
    },
    emptyTitle: { fontSize: 14, fontWeight: 600, color: t.text },
    skeleton: {
      height: 10,
      borderRadius: 4,
      background: t.chipBg,
    },
    footer: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minHeight: 48,
      padding: '6px 16px',
      borderTop: `1px solid ${t.border}`,
      background: t.panelBg,
      fontSize: 12.5,
      color: t.textSecondary,
      flexWrap: 'wrap',
    },
    footerStrong: { color: t.text, fontWeight: 600 },
    footerDot: { opacity: 0.5 },
    pageSize: {
      height: 26,
      padding: '0 8px',
      borderRadius: 6,
      border: `1px solid ${t.borderStrong}`,
      background: t.inputBg,
      fontFamily: MONO_FONT,
      fontSize: 11.5,
      color: t.text,
      gap: 4,
    },
    pager: { display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' },
    pageBtn: {
      minWidth: 28,
      height: 28,
      padding: '0 8px',
      borderRadius: 7,
      fontFamily: 'inherit',
      fontSize: 12,
      fontWeight: 600,
      color: t.textSecondary,
      border: `1px solid ${t.borderStrong}`,
      background: t.surface,
      '&:hover': { borderColor: t.accentBorder, color: t.text },
      '&.Mui-disabled': { opacity: 0.4 },
      '& svg': { fontSize: 16 },
    },
    pageActive: {
      color: '#fff',
      background: t.accentStrong,
      borderColor: t.accentStrong,
      '&:hover': { color: '#fff' },
    },
    menuPaper: {
      background: t.surface,
      border: `1px solid ${t.borderStrong}`,
      borderRadius: 10,
    },
    menuItem: { fontSize: 12.5, minHeight: 32, paddingLeft: 6 },
  };
});

const ownerTitleCache = new Map<string, string>();

function useOwnerTitles(entities: Entity[]) {
  const catalogApi = useApi(catalogApiRef);
  const [, setVersion] = useState(0);
  const refs = useMemo(() => {
    const set = new Set<string>();
    entities.forEach(e =>
      (e.relations ?? [])
        .filter(r => r.type === RELATION_OWNED_BY)
        .forEach(r => set.add(r.targetRef.toLocaleLowerCase('en-US'))),
    );
    return [...set];
  }, [entities]);

  useEffect(() => {
    const missing = refs.filter(r => !ownerTitleCache.has(r));
    if (!missing.length) return;
    catalogApi
      .getEntitiesByRefs({
        entityRefs: missing,
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'metadata.title', 'spec.profile.displayName'],
      })
      .then(response => {
        response.items.forEach((item, i) => {
          const profile = (item?.spec as any)?.profile;
          ownerTitleCache.set(
            missing[i],
            profile?.displayName ??
              item?.metadata.title ??
              humanizeName(shortRef(missing[i])),
          );
        });
        setVersion(v => v + 1);
      })
      .catch(() => undefined);
  }, [catalogApi, refs]);

  return (ref: string) =>
    ownerTitleCache.get(ref.toLocaleLowerCase('en-US')) ?? humanizeName(shortRef(ref));
}

function ownerRefOf(entity: Entity): string | undefined {
  return (entity.relations ?? []).find(r => r.type === RELATION_OWNED_BY)?.targetRef;
}

function systemRefOf(entity: Entity): string | undefined {
  const system = (entity.spec as any)?.system;
  if (!system || typeof system !== 'string') return undefined;
  try {
    return stringifyEntityRef(
      parseEntityRef(system, {
        defaultKind: 'system',
        defaultNamespace: entity.metadata.namespace ?? 'default',
      }),
    );
  } catch {
    return undefined;
  }
}

const OwnerLabel = ({
  entity,
  ownerTitle,
}: {
  entity: Entity;
  ownerTitle: (ref: string) => string;
}) => {
  const classes = useStyles();
  const ref = ownerRefOf(entity);
  if (!ref) return <span className={classes.muted}>—</span>;
  const isUser = ref.startsWith('user:');
  return (
    <span className={classes.owner} title={ref}>
      {isUser ? <PersonOutlineIcon /> : <GroupIcon />}
      {ownerTitle(ref)}
    </span>
  );
};

const StarButton = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const starred = isStarredEntity(entity);
  return (
    <Tooltip title={starred ? 'Quitar de favoritos' : 'Marcar como favorito'}>
      <IconButton
        size="small"
        className={`${classes.star} ${starred ? classes.starOn : ''}`}
        onClick={e => {
          e.stopPropagation();
          toggleStarredEntity(entity);
        }}
      >
        {starred ? <StarIcon /> : <StarBorderIcon />}
      </IconButton>
    </Tooltip>
  );
};

function useEntityHref() {
  const entityRoute = useRouteRef(entityRouteRef);
  return (entityOrRef: Entity | string) => entityRoute(entityRouteParams(entityOrRef));
}

const CellContent = ({
  column,
  entity,
  ownerTitle,
  hrefFor,
}: {
  column: ColumnId;
  entity: Entity;
  ownerTitle: (ref: string) => string;
  hrefFor: (e: Entity | string) => string;
}) => {
  const classes = useStyles();
  const spec = (entity.spec ?? {}) as Record<string, any>;
  switch (column) {
    case 'kind':
      return <FluxChip>{entity.kind.toLocaleLowerCase('en-US')}</FluxChip>;
    case 'system': {
      const ref = systemRefOf(entity);
      return ref ? (
        <FluxChip icon={<LayersIcon />} to={hrefFor(ref)} title={ref}>
          {parseEntityRef(ref).name}
        </FluxChip>
      ) : (
        <span className={classes.muted}>—</span>
      );
    }
    case 'owner':
      return <OwnerLabel entity={entity} ownerTitle={ownerTitle} />;
    case 'type':
      return spec.type ? (
        <FluxChip tone={spec.type === 'library' ? 'accent' : 'default'}>
          {String(spec.type)}
        </FluxChip>
      ) : (
        <span className={classes.muted}>—</span>
      );
    case 'lifecycle':
      return spec.lifecycle ? (
        <FluxChip tone={lifecycleTone(spec.lifecycle)}>{String(spec.lifecycle)}</FluxChip>
      ) : (
        <span className={classes.muted}>—</span>
      );
    case 'tags': {
      const tags = entity.metadata.tags ?? [];
      if (!tags.length) return <span className={classes.muted}>—</span>;
      return (
        <span className={classes.tagsCell}>
          {tags.slice(0, 3).map(tag => (
            <FluxChip key={tag}>{tag}</FluxChip>
          ))}
          {tags.length > 3 && <FluxChip>+{tags.length - 3}</FluxChip>}
        </span>
      );
    }
    default:
      return null;
  }
};

const EmptyResults = ({ onReset }: { onReset?: () => void }) => {
  const classes = useStyles();
  return (
    <div className={classes.empty}>
      <SearchIcon />
      <span className={classes.emptyTitle}>Sin resultados</span>
      <span>Ninguna entidad coincide con los filtros actuales.</span>
      {onReset && (
        <FluxButton size="small" onClick={onReset}>
          Limpiar filtros
        </FluxButton>
      )}
    </div>
  );
};

export const CatalogResults = (props: {
  state: CatalogQueryState;
  result: CatalogQueryResult;
  columns: ColumnId[];
  update: (patch: Partial<CatalogQueryState>, keepOffset?: boolean) => void;
  onReset: () => void;
  rowHref?: (entity: Entity) => string;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const hrefFor = useEntityHref();
  const rowHref = props.rowHref ?? hrefFor;
  const { state, result, columns, update } = props;
  const ownerTitle = useOwnerTitles(result.items);

  const toggleSort = (field: SortField) => {
    if (state.sortField === field) {
      update({ sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      update({ sortField: field, sortOrder: 'asc' });
    }
  };

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => {
    const active = state.sortField === field;
    return (
      <ButtonBase
        className={`${classes.sortBtn} ${active ? classes.sortActive : ''}`}
        onClick={() => toggleSort(field)}
      >
        {label}
        {active ? (
          <ArrowUpwardIcon className={state.sortOrder === 'desc' ? classes.sortDesc : ''} />
        ) : (
          <UnfoldMoreIcon />
        )}
      </ButtonBase>
    );
  };

  const showSkeleton = result.loading && result.items.length === 0;
  const isEmpty = !result.loading && !result.error && result.items.length === 0;

  return (
    <div className={classes.wrap}>
      {result.loading && <LinearProgress className={classes.progress} />}

      {state.view === 'list' && (
        <table className={classes.table}>
          <thead>
            <tr>
              <th>
                <SortHeader field="name" label="Name / Description" />
              </th>
              {columns.map(col => (
                <th key={col}>
                  {SORTABLE[col] ? (
                    <SortHeader field={SORTABLE[col]!} label={COLUMN_LABELS[col]} />
                  ) : (
                    COLUMN_LABELS[col]
                  )}
                </th>
              ))}
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <div className={classes.skeleton} style={{ width: '60%' }} />
                  </td>
                  {columns.map(col => (
                    <td key={col}>
                      <div className={classes.skeleton} style={{ width: 70 }} />
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
            {result.items.map(entity => {
              const Icon = entityIcon(entity);
              const href = rowHref(entity);
              return (
                <tr key={entity.metadata.uid ?? stringifyEntityRef(entity)} onClick={() => navigate(href)}>
                  <td>
                    <div className={classes.nameCell}>
                      <span className={classes.iconTile}>
                        <Icon />
                      </span>
                      <span className={classes.nameText}>
                        <span className={classes.name}>
                          {entity.metadata.title ?? entity.metadata.name}
                        </span>
                        {entity.metadata.description && (
                          <span className={classes.desc} title={entity.metadata.description}>
                            {entity.metadata.description}
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  {columns.map(col => (
                    <td key={col}>
                      <CellContent
                        column={col}
                        entity={entity}
                        ownerTitle={ownerTitle}
                        hrefFor={hrefFor}
                      />
                    </td>
                  ))}
                  <td>
                    <StarButton entity={entity} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {state.view === 'grid' && result.items.length > 0 && (
        <div className={classes.grid}>
          {result.items.map(entity => {
            const Icon = entityIcon(entity);
            const spec = (entity.spec ?? {}) as Record<string, any>;
            const systemRef = systemRefOf(entity);
            return (
              <ButtonBase
                key={entity.metadata.uid ?? stringifyEntityRef(entity)}
                className={classes.card}
                onClick={() => navigate(rowHref(entity))}
                component="div"
              >
                <div className={classes.cardHead}>
                  <span className={classes.iconTile}>
                    <Icon />
                  </span>
                  <span className={classes.nameText} style={{ flex: 1 }}>
                    <span className={classes.name}>
                      {entity.metadata.title ?? entity.metadata.name}
                    </span>
                  </span>
                  <StarButton entity={entity} />
                </div>
                <div className={classes.cardDesc}>
                  {entity.metadata.description ?? 'Sin descripción'}
                </div>
                <div className={classes.cardChips}>
                  {systemRef && (
                    <FluxChip icon={<LayersIcon />} to={hrefFor(systemRef)}>
                      {parseEntityRef(systemRef).name}
                    </FluxChip>
                  )}
                  {spec.type && (
                    <FluxChip tone={spec.type === 'library' ? 'accent' : 'default'}>
                      {String(spec.type)}
                    </FluxChip>
                  )}
                  {spec.lifecycle && (
                    <FluxChip tone={lifecycleTone(spec.lifecycle)}>
                      {String(spec.lifecycle)}
                    </FluxChip>
                  )}
                </div>
                <div className={classes.cardFoot}>
                  <OwnerLabel entity={entity} ownerTitle={ownerTitle} />
                </div>
              </ButtonBase>
            );
          })}
        </div>
      )}

      {isEmpty && <EmptyResults onReset={props.onReset} />}
    </div>
  );
};

function pageWindow(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set([0, total - 1, current - 1, current, current + 1]);
  const sorted = [...pages].filter(p => p >= 0 && p < total).sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('gap');
    out.push(p);
  });
  return out;
}

export const CatalogFooter = (props: {
  state: CatalogQueryState;
  result: CatalogQueryResult;
  update: (patch: Partial<CatalogQueryState>, keepOffset?: boolean) => void;
  labels?: { singular: string; plural: string };
}) => {
  const classes = useStyles();
  const { state, result, update } = props;
  const [sizeAnchor, setSizeAnchor] = useState<HTMLElement | null>(null);
  const total = result.totalItems;
  const page = Math.floor(state.offset / state.limit);
  const pageCount = Math.max(1, Math.ceil(total / state.limit));
  const from = total === 0 ? 0 : state.offset + 1;
  const to = Math.min(total, state.offset + state.limit);
  const labels = props.labels ?? kindLabels(state.kind);

  const goTo = (p: number) => update({ offset: p * state.limit }, true);

  return (
    <footer className={classes.footer}>
      <span>
        Mostrando{' '}
        <span className={classes.footerStrong}>
          {from}–{to}
        </span>{' '}
        de <span className={classes.footerStrong}>{total}</span>{' '}
        {total === 1 ? labels.singular : labels.plural}
      </span>
      <span className={classes.footerDot}>•</span>
      <span>
        Filas por página:{' '}
        <ButtonBase className={classes.pageSize} onClick={e => setSizeAnchor(e.currentTarget)}>
          {state.limit}
        </ButtonBase>
      </span>
      <Menu
        anchorEl={sizeAnchor}
        open={Boolean(sizeAnchor)}
        onClose={() => setSizeAnchor(null)}
        PaperProps={{ className: classes.menuPaper }}
      >
        {PAGE_SIZES.map(size => (
          <MenuItem
            key={size}
            className={classes.menuItem}
            selected={size === state.limit}
            onClick={() => {
              setSizeAnchor(null);
              update({ limit: size, offset: 0 });
            }}
          >
            &nbsp;{size}
          </MenuItem>
        ))}
      </Menu>
      <div className={classes.pager}>
        <ButtonBase className={classes.pageBtn} disabled={page === 0} onClick={() => goTo(page - 1)}>
          <ChevronLeftIcon /> Anterior
        </ButtonBase>
        {pageWindow(page, pageCount).map((p, i) =>
          p === 'gap' ? (
            <span key={`gap-${i}`} className={classes.footerDot}>
              …
            </span>
          ) : (
            <ButtonBase
              key={p}
              className={`${classes.pageBtn} ${p === page ? classes.pageActive : ''}`}
              onClick={() => goTo(p)}
            >
              {p + 1}
            </ButtonBase>
          ),
        )}
        <ButtonBase
          className={classes.pageBtn}
          disabled={page >= pageCount - 1}
          onClick={() => goTo(page + 1)}
        >
          Siguiente <ChevronRightIcon />
        </ButtonBase>
      </div>
    </footer>
  );
};

export const ColumnsMenu = (props: {
  kind: string;
  columns: ColumnId[];
  onChange: (columns: ColumnId[]) => void;
  anchor: HTMLElement | null;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const options = (Object.keys(COLUMN_LABELS) as ColumnId[]).filter(id =>
    columnApplies(id, props.kind),
  );
  return (
    <Menu
      anchorEl={props.anchor}
      open={Boolean(props.anchor)}
      onClose={props.onClose}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ className: classes.menuPaper }}
    >
      {options.map(id => {
        const checked = props.columns.includes(id);
        return (
          <MenuItem
            key={id}
            className={classes.menuItem}
            onClick={() =>
              props.onChange(
                checked ? props.columns.filter(c => c !== id) : [...props.columns, id],
              )
            }
          >
            <Checkbox size="small" color="primary" checked={checked} style={{ padding: 4 }} />
            {COLUMN_LABELS[id]}
          </MenuItem>
        );
      })}
    </Menu>
  );
};
